import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import type { Evaluation } from '@/types/evaluation.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RotateCcw, Info } from 'lucide-react';

interface ReactivateDialogProps {
  evaluation: Evaluation;
  onReactivate: (evaluation: Evaluation) => void;
  children: React.ReactNode;
}

export function ReactivateDialog({
  evaluation,
  onReactivate,
  children,
}: ReactivateDialogProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [open, setOpen] = useState(false);

  const handleReactivate = () => {
    onReactivate(evaluation);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-blue-600" aria-hidden="true" />
            {t('dashboard:status.reactivate.title')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard:status.reactivate.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current status info */}
          <div className="rounded-md bg-muted p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Current status:</span>
              <span className="font-medium capitalize">{evaluation.status}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status changed:</span>
              <span className="font-medium">
                {formatDistanceToNow(evaluation.statusChangedDate, { addSuffix: true })}
              </span>
            </div>
          </div>

          {/* Information message */}
          <div className="rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 p-4 flex items-start gap-3">
            <Info
              className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                {t('dashboard:status.reactivate.info')}
              </p>
              <ul className="text-sm text-blue-800 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Participants can create and edit scorecards again</li>
                <li>Scoring will be unlocked for all solutions</li>
                <li>You can modify criteria and solutions</li>
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleReactivate}>
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('dashboard:status.reactivate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
